import { Box, Chip, Popover, Stack } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { EnrStatusChip } from "./EnrStatusChip";
import {
  IEnrStatusChipVariant,
  IEnrStatusChipSize,
} from "./EnrStatusChip.types";
import { resolveVariantStyle } from "./EnrStatusChip.constants";

interface IEnrStatusChipGroupProps {
  data?: Array<string | null | undefined>;
  variant?: IEnrStatusChipVariant;
  size?: IEnrStatusChipSize;
  placeholder?: string;
}

export const EnrStatusChipGroup: React.FC<IEnrStatusChipGroupProps> = ({
  data,
  variant = "WARNING",
  size = "SM",
  placeholder = "--",
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const validData = useMemo(
    () =>
      (data ?? []).filter((v): v is string => Boolean(v && v.trim().length)),
    [data],
  );

  if (validData.length === 0) return <>{placeholder}</>;

  if (validData.length === 1) {
    return (
      <EnrStatusChip content={validData[0]} variant={variant} size={size} />
    );
  }

  const [first, ...rest] = validData;
  const open = Boolean(anchorEl);
  const styles = resolveVariantStyle(variant);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 1.2,
          backgroundColor: styles.highlightBg,
          border: `1px dashed ${styles.chipBorder}`,
        }}
      >
        <EnrStatusChip content={first} variant={variant} size={size} />
        <Chip
          size="small"
          onDelete={handleOpen}
          deleteIcon={<KeyboardArrowDown />}
          label={`+${rest.length}`}
          sx={{
            height: 22,
            backgroundColor: "transparent",
            border: "none",
            fontWeight: 700,
            color: styles.text,
            "& .MuiChip-deleteIcon": {
              color: styles.text,
            },
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 0.75,
            borderRadius: 2,
            backgroundColor: styles.popoverBg,
            border: `1px solid ${styles.chipBorder}`,
          },
        }}
      >
        <Stack spacing={0.5}>
          {rest.map((item, index) => (
            <EnrStatusChip
              key={index}
              content={item}
              variant={variant}
              size={size}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
};
